// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Getting between bays in an arcade that has people in it (M8-20).
//
// M5-23 built the gesture: look at a bay, pinch, stand there. It was correct in an empty room and
// it is the wrong shape in a full one, because **a bay holds one player**. `resolveBays` deals
// each participant a bay of their own and answers `null` when the room is full — "present,
// standing at no machine" — precisely so that two avatars are never drawn inside each other and
// two voices never come from one point in space. So the room, not the pinch, decides where a
// player ends up.
//
// **A teleport is therefore a request.** This file is the part that knows the difference between
// the three ways one can fail, because they are three different sentences to a player:
//
//   - you aimed at nothing — say nothing, because a control that announces its own misfires is
//     one a player learns to distrust;
//   - you aimed at the machine you are already standing at — also silence, for the same reason
//     M5-23 gives: a teleport that fires on the bay you are in appears broken;
//   - you aimed at a machine somebody else is playing — **say so**, because this is the only one
//     where the player did everything right and nothing happened.
//
// **And the room can move you when you did not ask.** Bays are resolved from the whole roster on
// every presence sync, and the tiebreak is the participant id rather than who arrived first: two
// players who pinch the same free bay in the same moment are separated afterwards, and one of them
// finds themselves somewhere they did not choose. That is not an error and cannot be prevented
// without a round trip on every teleport — what it needs is to be *told*, which is the last two
// sentences here.
//
// Pure: no Three.js, no presence, no clock. The runtime hands in what it knows and gets sentences
// and a decision back.

/** A bay, as navigation sees it: a number, and whether anybody is standing there. */
export interface BayStatus {
  readonly index: number
  /** The player standing there, or `''` for a free bay. */
  readonly occupant: string
  /** This is where the player asking is standing. */
  readonly here: boolean
}

/** Who is where, from the roster. `bay` is the *settled* bay, never the requested one. */
export interface BayOccupant {
  readonly displayName: string
  readonly bay: number | null | undefined
}

/**
 * The state of every bay in the room.
 *
 * Built for all `count` bays rather than only the occupied ones, because the menu draws a row per
 * bay and a list that omitted the free ones would be a list whose length changed as people came
 * and went — six rows that move under a fingertip.
 */
export function bayStatuses(
  count: number,
  playerBay: number,
  occupants: readonly BayOccupant[],
): readonly BayStatus[] {
  const taken = new Map<number, string>()
  for (const occupant of occupants) {
    const bay = occupant.bay
    if (bay === undefined || bay === null) continue
    if (!Number.isInteger(bay) || bay < 0 || bay >= count) continue
    // First writer wins. Two people cannot hold one bay — `resolveBays` guarantees it — so this
    // only matters for a roster caught mid-sync, where showing one name is better than showing a
    // name that flickers between two.
    if (!taken.has(bay)) taken.set(bay, occupant.displayName)
  }
  const rows: BayStatus[] = []
  for (let index = 0; index < count; index++) {
    rows.push({ index, occupant: taken.get(index) ?? '', here: index === playerBay })
  }
  return rows
}

/** What a bay's row says in the wrist menu. */
export function bayRowLabel(status: BayStatus): string {
  if (status.here) return `Machine ${status.index + 1} · you`
  if (status.occupant !== '') return `Machine ${status.index + 1} · ${status.occupant}`
  return `Machine ${status.index + 1}`
}

/** What asking to go to a bay does. */
export type BayMove =
  | { readonly kind: 'go'; readonly index: number }
  /** Somebody is playing it. The one refusal worth a sentence. */
  | { readonly kind: 'taken'; readonly who: string }
  /** Already there, or aimed at nothing. Both are silence. */
  | { readonly kind: 'stay' }

/**
 * May this player go to that bay?
 *
 * `undefined` for "aimed at nothing", which is a real answer from the look cone and not a missing
 * one — a player looking at the wall between two machines is looking at neither.
 */
export function bayMove(status: BayStatus | undefined): BayMove {
  if (status === undefined || status.here) return { kind: 'stay' }
  if (status.occupant !== '') return { kind: 'taken', who: status.occupant }
  return { kind: 'go', index: status.index }
}

/**
 * How long the room has to agree with a teleport before the client believes it disagreed, seconds.
 *
 * Two. A presence announcement is a round trip — track, sync, resolve — and one that has not come
 * back yet is indistinguishable from one that was refused. Two seconds is long enough for the round
 * trip on a bad connection and short enough that a genuinely refused teleport is corrected while
 * the player is still wondering why the machine looks wrong.
 */
export const BAY_SETTLE_SECONDS = 2

/** What to do about the bay the room says this player is standing at. */
export type RoomVerdict =
  /** The room agrees, or has not answered the question that was asked yet. */
  | 'nothing'
  /** It disagrees, but it is still answering the previous question. Give it a moment. */
  | 'wait'
  /** It disagrees and it has had time to hear us. Take its answer. */
  | 'move'

/**
 * Whether to believe the room over this client.
 *
 * **The wait is the whole of it.** Announcing a bay is a round trip, so for a beat after a teleport
 * the roster is still answering the *previous* question — and a client that believed it would be
 * dragged back to the bay it just left and then forward again, with a line saying the machine was
 * taken when nobody was there. After the grace period a disagreement is a real one: either somebody
 * beat this client to the bay, or the announcement never arrived, and both end the same way.
 *
 * `asked` is the bay this client last requested, or `undefined` when it has not asked for one —
 * which is every client that has not teleported since it arrived, and for those the room is simply
 * right.
 */
export function roomVerdict(
  settled: number,
  standing: number,
  asked: number | undefined,
  sinceAskSeconds: number,
): RoomVerdict {
  if (settled === standing) return 'nothing'
  if (asked !== undefined && sinceAskSeconds < BAY_SETTLE_SECONDS) return 'wait'
  return 'move'
}

/** What the toast says on arrival. */
export function arrivalLine(index: number): string {
  return `Moved to machine ${index + 1}.`
}

/** …and when the machine aimed at has somebody at it. */
export function takenLine(who: string): string {
  return `${who} is playing that machine.`
}

/**
 * …and when the room moved this player rather than the player moving themselves.
 *
 * **Named as the room's doing, not the player's.** "Moved to machine 3" after a player pinched
 * machine 3 is a confirmation; the same words after they pinched machine 4 and lost the tiebreak
 * would read as the machine having misheard them. It did not — somebody else was already there.
 */
export function movedByRoomLine(index: number): string {
  return `That machine was taken — you are at machine ${index + 1}.`
}

/**
 * …and when there was nowhere to put them at all.
 *
 * `resolveBays` answers `null` for the seventh player in a six-bay room: present in the venue and
 * standing at no machine. Worth its own sentence, because it is the one state where a player can
 * see other people and cannot get to a machine however they navigate.
 */
export const ARCADE_FULL_LINE = 'Every machine is taken — you are watching from the back.'
